/* Single API client. All API calls go through `api()` below.
   Base: /api/v1, cookies via credentials:'include'.
   Errors are thrown as HttpError parsed from {error:{code,message,details}}. */
import type { MeResponse, User } from './types';
import { localTimeZone } from '../lib/dateOnly';

export class HttpError extends Error {
  code: string;
  status: number;
  details?: unknown;

  constructor(message: string, code: string, status: number, details?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Registered by AuthContext: invoked once per 401 so the app can route to /login. */
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  unauthorizedHandler = fn;
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Any plain object; entries with undefined/null/'' values are dropped. */
  query?: object;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: object): string {
  const params = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    }
  }
  const qs = params.toString();
  return `/api/v1${path}${qs ? `?${qs}` : ''}`;
}

/**
 * R2-BR-D64: the API's validation envelope carries a generic top
 * message ("Request validation failed") plus zod's flattened field
 * errors, whose messages are the authored, user-meaningful ones
 * (e.g. "Rationale must be at least 10 characters"). When the top
 * message is that generic string, surface the first field/form error
 * instead so the user learns WHICH requirement failed.
 */
export function firstValidationMessage(details: unknown): string | null {
  if (!details || typeof details !== 'object') return null;
  const d = details as { formErrors?: unknown; fieldErrors?: unknown };
  if (Array.isArray(d.formErrors)) {
    for (const m of d.formErrors) if (typeof m === 'string' && m.trim()) return m;
  }
  if (d.fieldErrors && typeof d.fieldErrors === 'object') {
    for (const msgs of Object.values(d.fieldErrors as Record<string, unknown>)) {
      if (Array.isArray(msgs)) {
        for (const m of msgs) if (typeof m === 'string' && m.trim()) return m;
      }
    }
  }
  return null;
}

async function parseAndThrow(res: Response): Promise<never> {
  let code = 'REQUEST_FAILED';
  let message = `Request failed (${res.status})`;
  let details: unknown;
  try {
    const data = await res.json();
    if (data?.error) {
      code = data.error.code ?? code;
      message = data.error.message ?? message;
      details = data.error.details;
    }
  } catch {
    // non-JSON error body; keep defaults
  }
  if (message === 'Request validation failed') {
    const specific = firstValidationMessage(details);
    if (specific) message = specific;
  }
  const err = new HttpError(message, code, res.status, details);
  if (res.status === 401 && unauthorizedHandler) unauthorizedHandler();
  throw err;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = opts;
  const res = await fetch(buildUrl(path, query), {
    method,
    credentials: 'include',
    // Wave 3 (D03 residual A): API data is per-user and per-active-
    // beneficiary; a cached GET once served the previous beneficiary's
    // profile after a switch. Never satisfy an API read from cache.
    cache: 'no-store',
    signal,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    // Callers pass plain values; stringify defensively so a pre-stringified
    // body is never double-encoded into a JSON string on the wire.
    body: body !== undefined ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
  });
  if (!res.ok) await parseAndThrow(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** multipart upload (documents). Same error semantics as api(). */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!res.ok) await parseAndThrow(res);
  return (await res.json()) as T;
}

/** Download a file that requires the session cookie; returns a blob. */
export async function apiDownload(path: string): Promise<Blob> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'include', cache: 'no-store' });
  if (!res.ok) await parseAndThrow(res);
  return await res.blob();
}

// ---------------- Auth ----------------

export type IndividualPath = 'MYSELF' | 'MY_CHILD' | 'MY_STUDENT' | 'LIVING_EXPENSES';

export interface SignupBody {
  name: string;
  email: string;
  password: string;
  orgName?: string;
  kind?: 'ORGANIZATION' | 'INDIVIDUAL';
  individualPath?: IndividualPath;
}

export interface SignupResponse {
  user: User;
  organization: { id: string; name: string; plan: string; kind: string };
  membership: { role: string };
}

export interface LoginResponse extends MeResponse {}

export const auth = {
  signup: (body: SignupBody) =>
    api<SignupResponse>('/auth/signup', { method: 'POST', body }),
  login: (email: string, password: string) =>
    api<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
  logout: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => api<MeResponse>('/auth/me'),
  selectOrg: (organizationId: string) =>
    api<{ currentOrgId: string }>('/auth/select-org', {
      method: 'POST',
      body: { organizationId },
    }),
  createOrg: (name: string) =>
    api<{ organization: { id: string; name: string; plan: string; kind: string } }>('/auth/orgs', {
      method: 'POST',
      body: { name },
    }),
  demo: (persona: string) =>
    api<LoginResponse>('/auth/demo', {
      method: 'POST',
      body: { persona },
    }),
  oauthStatus: () => api<{ google: boolean; demo: boolean }>('/auth/oauth-status'),
  forgotPassword: (email: string) =>
    api<{ ok: true; mailerConfigured: boolean }>('/auth/forgot-password', {
      method: 'POST',
      body: { email },
    }),
  resetPassword: (token: string, password: string) =>
    api<{ ok: true }>('/auth/reset-password', {
      method: 'POST',
      body: { token, password },
    }),
};

// ---------------- Individual workflow ----------------

export type GradeLevel = 'K5' | '612' | 'HS' | 'HS_SENIOR' | 'UNDERGRAD' | 'GRAD' | 'ADULT';
export type EducationLevel = 'HIGH_SCHOOL' | 'UNDERGRAD' | 'GRADUATE' | 'TRADE' | 'ADULT_ED';
export type ApplicationStatus = 'SAVED' | 'IN_PROGRESS' | 'SUBMITTED' | 'WAITING' | 'AWARDED' | 'NOT_AWARDED';

/** Round 5: individual tracks, urgency, beneficiaries, and aid states. */
export type IndividualTrack = 'EDUCATION' | 'LIVING_EXPENSES';
export type Urgency = 'TODAY' | 'DEADLINE_APPROACHING' | 'PLANNING' | 'NOT_SURE';
export type BeneficiaryRelationship = 'SELF' | 'CHILD' | 'STUDENT' | 'HOUSEHOLD_MEMBER';
export type SharingBasis = 'ACCOUNT_HOLDER' | 'PARENT_MANAGED' | 'CONSENTED';
export type ProgramLevel = 'NATIONAL' | 'STATE' | 'LOCAL_INTAKE' | 'SPONSOR';
export type IntakeAvailability = 'OPEN' | 'WAITLIST' | 'CLOSED' | 'SEASONAL' | 'UNKNOWN';
export type ReviewStatus = 'VERIFIED' | 'UNVERIFIED' | 'QUARANTINED';
export type AiPolicy = 'ALLOWED' | 'PROHIBITED' | 'UNKNOWN';
export type FafsaState =
  | 'NOT_STARTED' | 'PREPARING' | 'STARTED_EXTERNALLY' | 'SUBMITTED'
  | 'PROCESSING' | 'ACTION_NEEDED' | 'PROCESSED';
export type ContactState =
  | 'NOT_CONTACTED' | 'CONTACTED' | 'APPOINTMENT' | 'PREPARING' | 'SUBMITTED'
  | 'INFO_REQUESTED' | 'WAITLISTED' | 'DECIDED' | 'RECEIVED';

export interface Beneficiary {
  id: string;
  displayName: string;
  relationship: BeneficiaryRelationship;
  sharingBasis: SharingBasis;
  gradeLevel: GradeLevel | null;
  educationLevel: EducationLevel | null;
  state: string | null;
  school: string | null;
  fieldOfStudy: string | null;
  birthYear: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BeneficiaryInput {
  displayName: string;
  relationship: BeneficiaryRelationship;
  sharingBasis?: SharingBasis;
  gradeLevel?: GradeLevel | null;
  educationLevel?: EducationLevel | null;
  state?: string | null;
  school?: string | null;
  fieldOfStudy?: string | null;
  birthYear?: number | null;
  notes?: string | null;
}

/** Decision & award record (education applications). Money fields are nullable. */
export interface DecisionJson {
  grantsEstimated?: number | null;
  grantsOffered?: number | null;
  grantsAccepted?: number | null;
  grantsReceived?: number | null;
  loans?: number | null;
  workStudy?: number | null;
  conditions?: string | null;
  recipient?: string | null;
  period?: string | null;
  renewalSteps?: string | null;
}

/** Contact tracker (assistance applications). */
export interface ContactJson {
  state?: ContactState;
  contactedAt?: string | null;
  method?: string | null;
  appointmentAt?: string | null;
  followUpAt?: string | null;
  providerNote?: string | null;
  outcomeNote?: string | null;
  /** Batch 2 Item 4: structured outcome (proposals §4c). Stage/paidTo
   *  semantics follow the funding ledger — RECEIVED + paid to a
   *  provider is real support, never cash the user received. */
  benefitStage?: 'OFFERED' | 'APPROVED' | 'ACCEPTED' | 'RECEIVED' | null;
  benefitAmount?: number | null;
  benefitKind?: 'CASH' | 'NONCASH' | null;
  noncashDescription?: string | null;
  receivedAmount?: number | null;
  paidTo?: 'USER' | 'LANDLORD' | 'UTILITY_COMPANY' | 'PROVIDER' | 'OTHER' | null;
  recipientNote?: string | null;
  assistancePeriodStart?: string | null;
  assistancePeriodEnd?: string | null;
}

export interface IndividualNextStep {
  kind: string;
  title: string;
  reason: string;
  href: string;
  rank: number;
  /** R2-BR-D11: stable deferral key + data fingerprint + urgency flag. */
  key: string;
  fingerprint: string;
  urgent: boolean;
  /** R2-BR-D72: why a deferred step is still shown (single-step plans). */
  deferralNote?: string | null;
  /** D79: deadline steps carry their deadline; the title is count-free
   *  and the web renders the day count (see nextStepTitle). */
  deadline?: string | null;
}

export interface IndividualHomeResponse {
  profile: IndividualProfile | null;
  activeBeneficiary: Beneficiary | null;
  nextSteps: IndividualNextStep[];
  /** R2-BR-D11: deferred-but-urgent steps whose marker stays visible. */
  urgentMarkers: IndividualNextStep[];
  resume: { onboardingStep: number; beneficiaryId: string | null; completed?: boolean };
}

export interface IndividualProfile {
  id: string;
  organizationId: string;
  path: IndividualPath;
  subjectName: string;
  subjectLabel: string | null;
  birthYear: number | null;
  gradeLevel: GradeLevel | null;
  educationLevel: EducationLevel | null;
  state: string | null;
  school: string | null;
  fieldOfStudy: string | null;
  needMarkers: string[];
  /** Optional self-reported demographics. race is a multi-select; the rest are single values. */
  demographics: Record<string, string | string[]> | null;
  /** Living-expenses screener fields (LIVING_EXPENSES path). */
  householdSize: number | null;
  incomeBand: 'UNDER_30K' | '30K_60K' | 'OVER_60K' | 'PREFER_NOT_TO_SAY' | null;
  assistanceNeeds: string[];
  hasChildren: boolean | null;
  notes: string | null;
  completedAt: string | null;
  /** Round 5 (all optional; older payloads may omit them). */
  zip?: string | null;
  county?: string | null;
  fundingYear?: string | null;
  urgency?: Urgency | null;
  enabledTracks?: IndividualTrack[];
  onboardingStep?: number;
  beneficiaryId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IndividualProfileInput {
  path: IndividualPath;
  subjectName?: string;
  subjectLabel?: string;
  birthYear?: number | null;
  gradeLevel?: GradeLevel | null;
  educationLevel?: EducationLevel | null;
  state?: string | null;
  school?: string | null;
  fieldOfStudy?: string | null;
  needMarkers?: string[];
  /** Optional self-reported demographics. race is a multi-select; the rest are single values. */
  demographics?: Record<string, string | string[]> | null;
  /** Living-expenses screener fields (LIVING_EXPENSES path). */
  householdSize?: number | null;
  incomeBand?: 'UNDER_30K' | '30K_60K' | 'OVER_60K' | 'PREFER_NOT_TO_SAY' | null;
  assistanceNeeds?: string[];
  hasChildren?: boolean | null;
  notes?: string | null;
  completed?: boolean;
  /** Round 5. */
  zip?: string | null;
  county?: string | null;
  fundingYear?: string | null;
  urgency?: Urgency | null;
  enabledTracks?: IndividualTrack[];
  onboardingStep?: number;
  beneficiaryId?: string | null;
}

export interface EligibilityMatchItem {
  label: string;
  verdict: 'met' | 'unmet' | 'unknown' | 'info' | 'not_applicable';
  /** R2-BR-D07: stage-based reason when verdict is 'not_applicable'. */
  reason?: string;
  /** R2-BR-D31: for 'unknown', what profile answer would let us check it. */
  resolveHint?: string;
  /**
   * R2-BR-D84: for 'unknown', which kind — 'profile' (a profile
   * answer resolves it) or 'unverifiable' (a listed requirement
   * Navigator cannot check from the profile at all; surfaces must
   * present it as "Not checked — verify", never as verified and
   * never as profile homework).
   */
  unknownKind?: 'profile' | 'unverifiable';
}

export interface EligibilityMatch {
  score: number | null;
  met: number;
  unmet: number;
  unknown: number;
  /**
   * R2-BR-D84: the unknown split — matcher-bearing criteria the
   * profile doesn't answer yet vs listed requirements Navigator
   * cannot check at all. `unknown` remains their sum. Optional only
   * for tolerance of older payloads; surfaces derive the split from
   * item kinds when absent (pages/individual matchUnknownSplit).
   */
  unknownProfile?: number;
  unknownUnverifiable?: number;
  /** R2-BR-D07: criteria scoped out for this profile (never scored). */
  notApplicable?: number;
  total: number;
  items: EligibilityMatchItem[];
  /**
   * R2-BR-D31: the unknown criteria, NAMED, each with what would
   * resolve it. Surfaces presenting the score while this is non-empty
   * must qualify the certainty and name these — never a bare score.
   */
  unknownCriteria?: Array<{ label: string; resolveHint: string }>;
  /**
   * R2-BR-D07: program-level stage applicability — true when this is a
   * college-stage program and the profile is for a pre-college (K5/612)
   * beneficiary. Rendered surfaces must show this (with the reason),
   * never collapse it into the generic unknown-match state.
   */
  stageNotApplicable?: boolean;
  notApplicableReason?: string | null;
  /**
   * RC.9 (D78): the score's evidence basis, same shape as the org
   * side's scoreBasis — evaluated = met + unmet; applicable = met +
   * unmet + unknown. Optional only for tolerance of older payloads;
   * surfaces derive it from the counts when absent (pages/individual
   * matchScoreBasis). `total` counts CHECKED criteria only and must
   * never be used as the basis denominator.
   */
  scoreBasis?: { evaluated: number; applicable: number } | null;
}

export interface IndividualOpportunity {
  id: string;
  title: string;
  agency: string | null;
  sourceUrl: string | null;
  noticeType: string | null;
  estValueMin: number | null;
  estValueMax: number | null;
  responseDeadline: string | null;
  /** Deadline state (release acceptance): 'DATED' | 'ROLLING' |
   *  'UNPUBLISHED' | 'UNKNOWN'. A date exists iff DATED. */
  deadlineKind?: string | null;
  deadlineNote?: string | null;
  /** Batch 3 Stage 1: structured cycle data + its relation to the
   *  profile's funding-year preference (API lib/cycle). Windows carry
   *  ISO calendar dates ('YYYY-MM-DD') only where the listing's own
   *  record states a day. */
  cycle?: {
    label: string | null;
    kind: string | null;
    windows: { label?: string; opensAt?: string; closesAt?: string }[];
    note: string | null;
    relation: 'CURRENT_CYCLE' | 'NEXT_CYCLE_NOT_OPEN' | 'DIFFERENT_CYCLE' | 'UNKNOWN';
  } | null;
  description: string | null;
  educationLevels: string[];
  individualPaths: IndividualPath[];
  needCategories: string[];
  eligibilityMatch: EligibilityMatch;
  /** QA B-D3: true when the catalog deadline has passed for this cycle. */
  deadlinePassed?: boolean;
  /** Round 5 trust/evidence fields (optional; older payloads may omit them). */
  programLevel?: ProgramLevel | null;
  intakeAvailability?: IntakeAvailability | null;
  availabilityNote?: string | null;
  applyText?: string | null;
  sourceCheckedAt?: string | null;
  reviewStatus?: ReviewStatus | null;
  /** R2-BR-D40: detail responses carry the existing tracked record's id
      (null when this workspace has not started/tracked the listing). */
  existingApplicationId?: string | null;
  /** Batch 3 Stage 2 (assistance service areas): coverage model.
   *  coverageType null = UNCONFIRMED (never guessed). serviceStates
   *  lists the states a listing is restricted to (empty = not
   *  state-restricted); a non-empty list is already reflected in
   *  eligibilityMatch via the state matcher. Older payloads may omit
   *  both fields. */
  coverageType?: 'NATIONAL' | 'STATE_ADMINISTERED' | 'LOCAL_NETWORK' | 'SPONSOR_DECIDES' | null;
  serviceStates?: string[];
}

export interface IndividualOpportunitiesParams {
  q?: string;
  path?: IndividualPath;
  educationLevel?: EducationLevel;
  type?: string;
  need?: string;
  sort?: 'match' | 'deadline' | 'value' | 'updated';
  page?: number;
  limit?: number;
}

export interface ChecklistItem {
  label: string;
  done: boolean;
}

export interface GrantApplication {
  id: string;
  organizationId: string;
  opportunityId: string;
  status: ApplicationStatus;
  checklistJson: ChecklistItem[];
  notes: string | null;
  essayPrompt: string | null;
  essayDraft: string | null;
  /** QA B-D5 completion: program essay word limit persisted per application (server). */
  essayWordLimit?: number | null;
  /** Quill interview answers: [{ question, answer }] — the applicant's own words. */
  essayInterview: { question: string; answer: string }[] | null;
  deadline: string | null;
  reminderAt: string | null;
  submittedAt: string | null;
  /** Batch 2 Item 3: the provider's confirmation/reference number, as
   *  reported by the user (never verified), and the uploaded receipt
   *  file's metadata. */
  submissionReference?: string | null;
  submissionReceipt?: {
    fileName: string;
    storageKey: string;
    mimeType: string | null;
    sizeBytes: number;
    uploadedAt: string;
  } | null;
  /** Round 5 (optional; older payloads may omit them). */
  beneficiaryId?: string | null;
  beneficiary?: { id: string; displayName: string } | null;
  aiPolicy?: AiPolicy | null;
  fafsaState?: FafsaState | null;
  decisionJson?: DecisionJson | null;
  contactJson?: ContactJson | null;
  createdAt: string;
  updatedAt: string;
  opportunity: {
    id: string;
    title: string;
    agency: string | null;
    sourceUrl: string | null;
    noticeType: string | null;
    estValueMin: number | null;
    estValueMax: number | null;
    responseDeadline: string | null;
    needCategories: string[];
    eligibilityJson: unknown;
    programLevel?: ProgramLevel | null;
    intakeAvailability?: IntakeAvailability | null;
    availabilityNote?: string | null;
    sourceCheckedAt?: string | null;
    reviewStatus?: ReviewStatus | null;
  };
}

/** One Quill interview question, tailored to the grant type. */
export interface InterviewQuestion {
  id: string;
  question: string;
  hint?: string;
}

function qs(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

/** Batch 3 Stage 3 — saved searches + daily digests (all tiers).
 *  A saved search is personal to its creator inside the current
 *  workspace; its digest covers only genuinely new matches, once. */
export interface SavedSearchQueryInput {
  q?: string;
  type?: string;
  source?: string;
  naics?: string;
  setAside?: string;
  path?: string;
  educationLevel?: string;
  need?: string;
  beneficiaryId?: string;
}

export interface SavedSearchItem {
  id: string;
  name: string;
  context: 'ORG' | 'INDIVIDUAL';
  query: SavedSearchQueryInput;
  digestEnabled: boolean;
  createdAt: string;
  lastDigestSentAt: string | null;
}

export const savedSearches = {
  list: () => api<{ items: SavedSearchItem[]; limit: number }>('/saved-searches'),
  create: (body: { name: string; query: SavedSearchQueryInput; digestEnabled?: boolean }) =>
    api<SavedSearchItem & { currentMatchCount: number }>('/saved-searches', { method: 'POST', body }),
  update: (id: string, body: { name?: string; digestEnabled?: boolean }) =>
    api<SavedSearchItem>(`/saved-searches/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<void>(`/saved-searches/${id}`, { method: 'DELETE' }),
};

export const individual = {
  getProfile: () => api<{ profile: IndividualProfile | null }>('/individual/profile'),
  saveProfile: (body: IndividualProfileInput) =>
    api<{ profile: IndividualProfile }>('/individual/profile', { method: 'PUT', body }),
  opportunities: (params: IndividualOpportunitiesParams = {}) =>
    api<{
      items: IndividualOpportunity[];
      page: number;
      limit: number;
      total: number;
      /** R2-BR-D32: per-type counts over the FULL filtered set (same
          source as `total`), so sub-counts always sum to the total. */
      typeCounts: Record<string, number>;
    }>(
      `/individual/opportunities${qs(params as Record<string, string | number | undefined>)}`,
    ),
  opportunity: (id: string) => api<IndividualOpportunity>(`/individual/opportunities/${id}`),
  applications: (status?: ApplicationStatus, page?: number, limit?: number, beneficiaryId?: string) =>
    api<{ items: GrantApplication[]; page: number; limit: number; total: number }>(
      `/individual/applications${qs({ status, page, limit, beneficiaryId } as Record<string, string | number | undefined>)}`,
    ),
  application: (id: string) => api<GrantApplication>(`/individual/applications/${id}`),
  saveApplication: (opportunityId: string) =>
    api<GrantApplication>('/individual/applications', { method: 'POST', body: { opportunityId } }),
  updateApplication: (id: string, body: Partial<Pick<GrantApplication, 'status' | 'notes' | 'essayPrompt' | 'essayDraft' | 'essayWordLimit' | 'deadline' | 'reminderAt'>> & {
    checklist?: ChecklistItem[];
    essayInterview?: { question: string; answer: string }[] | null;
    beneficiaryId?: string | null;
    aiPolicy?: AiPolicy | null;
    fafsaState?: FafsaState | null;
    decisionJson?: DecisionJson | null;
    contactJson?: ContactJson | null;
    // Batch 2 Item 3: submission reference + a corrected submitted date.
    submissionReference?: string | null;
    submittedAt?: string;
  }) =>
    api<GrantApplication>(`/individual/applications/${id}`, { method: 'PATCH', body }),
  deleteApplication: (id: string) =>
    api<{ ok: true }>(`/individual/applications/${id}`, { method: 'DELETE' }),
  interviewQuestions: (id: string) =>
    api<{ questions: InterviewQuestion[]; savedAnswers: { question: string; answer: string }[] }>(
      `/individual/applications/${id}/interview-questions`,
    ),
  draftEssay: (
    id: string,
    prompt: string,
    interview?: { question: string; answer: string }[],
    opts?: { wordLimit?: number | null; includeProfileNotes?: boolean; unknownAiPolicyConfirmed?: boolean },
  ) =>
    api<{
      essayPrompt: string; essayDraft: string; message: string;
      wordLimit?: number | null; wordCount?: number; overLimit?: boolean; trimmedDraft?: string; draftingFor?: string | null;
    }>(`/individual/applications/${id}/essay-draft`, {
      method: 'POST',
      body: {
        prompt,
        ...(interview && interview.length > 0 ? { interview } : {}),
        ...(opts?.wordLimit ? { wordLimit: opts.wordLimit } : {}),
        ...(opts?.includeProfileNotes ? { includeProfileNotes: true } : {}),
        // R2-BR-D09: the per-application UNKNOWN-policy confirmation —
        // sent only when the user has actually confirmed; the server
        // refuses UNKNOWN-policy drafting without it.
        ...(opts?.unknownAiPolicyConfirmed ? { unknownAiPolicyConfirmed: true } : {}),
      },
    }),
  // D79: the viewer's IANA timezone rides along so the server's
  // deadline windows/urgent flags use the same calendar "today" the
  // web renders counts against.
  home: () => api<IndividualHomeResponse>(`/individual/home?tz=${encodeURIComponent(localTimeZone())}`),
  /** R2-BR-D11: persist a "Not now" deferral; returns the refreshed Home payload. */
  deferNextStep: (body: { key: string; fingerprint: string }) =>
    api<IndividualHomeResponse>(`/individual/home/defer?tz=${encodeURIComponent(localTimeZone())}`, { method: 'POST', body }),
  beneficiaries: () => api<{ items: Beneficiary[] }>('/individual/beneficiaries'),
  createBeneficiary: (body: BeneficiaryInput) =>
    api<Beneficiary>('/individual/beneficiaries', { method: 'POST', body }),
  updateBeneficiary: (id: string, body: Partial<BeneficiaryInput>) =>
    api<Beneficiary>(`/individual/beneficiaries/${id}`, { method: 'PATCH', body }),
  deleteBeneficiary: (id: string) =>
    api<{ ok: true }>(`/individual/beneficiaries/${id}`, { method: 'DELETE' }),
  reportOpportunity: (id: string, body: { reason: string; detail?: string }) =>
    api<{ ok: true }>(`/individual/opportunities/${id}/report`, { method: 'POST', body }),
  billing: () =>
    api<{ plan: string; status: string; currentPeriodEnd: string | null; price: string }>('/individual/billing'),
  /** Batch 2 Item 1: a Stripe Billing Portal session for the subscriber's own workspace. */
  billingPortal: () => api<{ url: string }>('/individual/billing/portal', { method: 'POST', body: {} }),
  /** Batch 2 Item 3: the printable submission record + the provider receipt file. */
  submissionRecordPdf: (id: string) => apiDownload(`/individual/applications/${id}/submission-receipt.pdf`),
  uploadSubmissionReceipt: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiUpload<GrantApplication>(`/individual/applications/${id}/submission-receipt-file`, fd);
  },
  downloadSubmissionReceipt: (id: string) => apiDownload(`/individual/applications/${id}/submission-receipt-file`),
  removeSubmissionReceipt: (id: string) =>
    api<{ ok: true }>(`/individual/applications/${id}/submission-receipt-file`, { method: 'DELETE' }),
  billingCheckout: () => api<{ url: string }>('/individual/billing/checkout', { method: 'POST' }),
};
